import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { formatNumber } from "helpers/getNumberFormats";
import { getIssueDateOptions } from "./issueDateOptions";
import { CORPORATE_BLUE } from "./invoiceLayout";
import { isInvalidQuantity, MIN_INVOICE_QUANTITY } from "./validateQuantity";
import { sumBreakdownRows } from "./invoiceTotals";

const BREAKDOWN_STEP = "breakdown";
const ISSUE_DATE_STEP = "issueDate";

const actionButtonStyle = {
  textTransform: "none",
  minWidth: 120,
};

/**
 * Timbrado en dos pasos (CRM-3108):
 *   1. La tabla de lo que se va a facturar, un renglon por permiso y metodo
 *      de pago. El boton dice Siguiente.
 *   2. La fecha de emision, limitada a las ultimas 72 horas. El boton dice
 *      Timbrar y solo aparece cuando ya hay fecha elegida.
 */
export const ConfirmBreakdownModal = memo(
  ({ open, onClose, onSubmit, isLoading, selectedData }) => {
    const [step, setStep] = useState(BREAKDOWN_STEP);
    const [issueDate, setIssueDate] = useState(null);

    const sortedData = useMemo(
      () => [...selectedData].sort((a, b) => b.taxRate - a.taxRate),
      [selectedData],
    );

    // El SAT no acepta partidas por debajo de 1 litro (CRM-3022): en esta
    // tabla ya solo aparecen renglones con cantidad real (buildBreakdownModalRows
    // descarta los metodos sin tocar), asi que cualquier cantidad aqui
    // cuenta para la validacion.
    const hasInvalidQuantity = useMemo(
      () => sortedData.some((row) => isInvalidQuantity(row.quantity)),
      [sortedData],
    );

    const totals = useMemo(() => sumBreakdownRows(sortedData), [sortedData]);

    const dateOptions = useMemo(() => (open ? getIssueDateOptions() : []), [open]);

    // Cada apertura arranca de cero: no se hereda la fecha del intento previo.
    useEffect(() => {
      if (!open) {
        setStep(BREAKDOWN_STEP);
        setIssueDate(null);
      }
    }, [open]);

    const isIssueDateStep = step === ISSUE_DATE_STEP;

    const handleConfirm = () => {
      if (!isIssueDateStep) {
        if (hasInvalidQuantity) return;
        return setStep(ISSUE_DATE_STEP);
      }
      if (issueDate) onSubmit(issueDate.date);
    };

    return (
      <Dialog
        open={open}
        onClose={isLoading ? undefined : onClose}
        // El paso de la tabla se ajusta al ancho real de sus columnas
        // (maxWidth=false deja que el Paper mida su contenido) en vez de
        // forzar siempre "md"; el paso de la fecha si necesita un ancho fijo
        // y compacto porque solo trae el autocomplete.
        maxWidth={isIssueDateStep ? "xs" : false}
        fullWidth={isIssueDateStep}
        // El tope de 95vw solo aplica al paso de la tabla, para que una
        // tabla ancha no se salga del viewport. En el paso de la fecha no
        // aporta nada -y competia en la cascada con el ancho compacto "xs",
        // ensanchando el modal casi a pantalla completa en monitores grandes.
        PaperProps={isIssueDateStep ? undefined : { sx: { maxWidth: "95vw" } }}
      >
        <DialogTitle
          sx={{
            py: 2.5,
            textAlign: "center",
            fontWeight: 600,
            color: CORPORATE_BLUE,
          }}
        >
          {isIssueDateStep
            ? "Fecha de Emisión"
            : "¿Estás seguro que deseas facturar con estos valores?"}
        </DialogTitle>

        <DialogContent sx={{ overflowX: "hidden" }}>
          {!isIssueDateStep && (
            <Slide direction="right" in={!isIssueDateStep} appear={false}>
              <Box sx={{ my: 2 }}>
                {hasInvalidQuantity && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {`Hay partidas con menos de ${MIN_INVOICE_QUANTITY} Lt; corrígelas para poder timbrar.`}
                  </Typography>
                )}
                {sortedData.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "grey.100" }}>
                        <TableRow>
                          <TableCell><b>M. Pago</b></TableCell>
                          <TableCell><b>PERMISO</b></TableCell>
                          <TableCell><b>TIPO</b></TableCell>
                          <TableCell><b>PU</b></TableCell>
                          <TableCell align="right"><b>TASA</b></TableCell>
                          <TableCell align="right"><b>CANTIDAD</b></TableCell>
                          <TableCell align="right"><b>SUBTOTAL</b></TableCell>
                          <TableCell align="right"><b>TOTAL</b></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedData.map((row, idx) => {
                          const invalidQuantity = isInvalidQuantity(row.quantity);
                          return (
                            <TableRow
                              key={`${row.creLicense}_${row.taxRate}_${idx}`}
                              sx={invalidQuantity ? { backgroundColor: "#fdecea" } : undefined}
                            >
                              <TableCell>{row.name}</TableCell>
                              <TableCell>{row.creLicense}</TableCell>
                              <TableCell>
                                <Chip
                                  label={row.isManualInvoice ? "Manual" : "Automática"}
                                  size="small"
                                  color={row.isManualInvoice ? "primary" : "default"}
                                />
                              </TableCell>
                              <TableCell>$ {row.unitPrice}</TableCell>
                              <TableCell align="right">{row.taxRate}%</TableCell>
                              <TableCell
                                align="right"
                                sx={invalidQuantity ? { color: "#d32f2f", fontWeight: 600 } : undefined}
                              >
                                {formatNumber({ input: row.quantity, decimals: 2 })}
                              </TableCell>
                              <TableCell align="right">
                                ${formatNumber({ input: row.subtotal })}
                              </TableCell>
                              <TableCell align="right">
                                ${formatNumber({ input: row.total })}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      {sortedData.length > 1 && (
                        <TableBody>
                          <TableRow sx={{ backgroundColor: "grey.100" }}>
                            <TableCell colSpan={5}>
                              <b>TOTAL</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>{formatNumber({ input: totals.quantity, decimals: 2 })}</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>${formatNumber({ input: totals.subtotal })}</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>${formatNumber({ input: totals.total })}</b>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Slide>
          )}

          {isIssueDateStep && (
            <Slide direction="left" in={isIssueDateStep}>
              <Box sx={{ my: 2 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Elige la fecha con la que se timbrarán las notas. Solo se
                  permiten las últimas 72 horas.
                </Typography>

                <AutocompleteComponent
                  id="global-invoice-issue-date"
                  name="issueDate"
                  label="Fecha de emisión"
                  options={dateOptions}
                  value={issueDate}
                  fullWidth
                  size="small"
                  disabled={isLoading}
                  handleValues={({ issueDate: selected }) =>
                    setIssueDate(selected)
                  }
                  noOptionsText="No hay fechas disponibles"
                />
              </Box>
            </Slide>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", gap: 1, px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="medium"
            onClick={onClose}
            disabled={isLoading}
            sx={actionButtonStyle}
          >
            Cancelar
          </Button>

          {/* En el paso de la fecha el boton no se muestra hasta que hay una. */}
          {(!isIssueDateStep || issueDate) && (
            <Button
              variant="contained"
              size="medium"
              onClick={handleConfirm}
              disabled={isLoading || (!isIssueDateStep && hasInvalidQuantity)}
              startIcon={
                isIssueDateStep && isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                ...actionButtonStyle,
                backgroundColor: CORPORATE_BLUE,
                "&:hover": { backgroundColor: CORPORATE_BLUE },
              }}
            >
              {isIssueDateStep && isLoading
                ? "Timbrando..."
                : isIssueDateStep
                  ? "Timbrar"
                  : "Siguiente"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);
